/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';

// redux
import { useDispatch, useSelector } from "react-redux";
import {resetToDB, updateCachedCompanyInfo, updateUpdateStatus} from "../../../../../state/slices/account";
import {updateAccountDetails} from "../../../../../state/thunks/account";

// material
import { TextField, SingleSelectDropDown } from "@insticator/insticator-ui";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
import Field from "./field";

// consts
import {ACCOUNT_GRADES, ACTIVE_STATUS, FIELD_MAPPING, SELLER_TYPES} from "./companyinfo_const";

// styles
import injectSheet from 'react-jss';
import styles from './companyinfo_styles';

// static
const SECTION_TITLE = 'Company Info'
const UPDATE_STATUS = 'companyInfoUpdateStatus'
const STATUS_IDLE = 'idle'
const SUCCESS_MESSAGE = 'Update Success'
const ERROR_MESSAGE = 'Update Failed'

/* ========== ~~~~~~~~~~ COMPANY INFO ~~~~~~~~~~ ========== */
const CompanyInfo = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const {
    account: {
      cachedCompanyInfo,
      companyInfoFromDB,
      companyInfoUpdateStatus
    },
    session: {
      accountUUID
    }
  } = useSelector((state) => state)
  const [isOpened, setIsOpened] = useState(false)
  const [isChanged, setIsChanged] = useState(false)

  /*************** ASYNC ***************/
  useEffect(() => {
    if (companyInfoUpdateStatus === 'success' || companyInfoUpdateStatus === 'error') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    }, [2000])
  }, [companyInfoUpdateStatus])

  useEffect(() => {
    if (!cachedCompanyInfo) return
    let newIsChanged = false
    Object.entries(cachedCompanyInfo)
      .forEach(([key, value]) => {
        if (companyInfoFromDB[key] !== value) newIsChanged = true
      })
    setIsChanged(newIsChanged)
  }, [cachedCompanyInfo])

  /*************** EVENT HANDLERS ***************/
  const handleToggle = () => {
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    setIsOpened(!isOpened)
  }

  const handleCancel = () => {
    setIsOpened(false)
    dispatch(resetToDB({ cache: 'cachedCompanyInfo', db: 'companyInfoFromDB' }))
  }

  const handleSave = () => {
    setIsOpened(false)
    dispatch(updateAccountDetails({ accountUUID, cachedCompanyInfo }))
  }

  const handleChange = e => {
    dispatch(updateCachedCompanyInfo({"field": e.target.name, "value": e.target.value}))
  }

  const handleAccountGradeSelect = e => {
    dispatch(updateCachedCompanyInfo({"field": "accountGrade", "value": e.value}))
  }

  const handleStatusSelect = e => {
    dispatch(updateCachedCompanyInfo({"field": "isActive", "value": e.value}))
  }

  const handleAdInventorySelect = e => {
    dispatch(updateCachedCompanyInfo({"field": "sellerType", "value": e.value}))
  }

  /*************** CONTROLLER & COMPONENT ***************/
  let statusMessage = "";
  switch (companyInfoUpdateStatus) {
    case "success":
      statusMessage = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessage = ERROR_MESSAGE
      break;
    case "idle":
      statusMessage = ""
      break;
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      title={SECTION_TITLE}
      description={statusMessage}
      descriptionClass={companyInfoUpdateStatus === "success" ? props.classes.updateSuccess : props.classes.updateFailed}
      />
  )

  return (
    <ExpandableSection
      className={`companyinfo ${props.classes.companyInfo}`}
      controller={controller}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      >
      {cachedCompanyInfo && <>
        <SectionCard klass={'sectionCard'}>
          <Field title={FIELD_MAPPING['accountName']} content={<TextField name={'accountName'} klass={'text'}
                                                            value={cachedCompanyInfo.accountName} handleChange={handleChange} />} />
          <Field title={FIELD_MAPPING['businessDomain']}  content={<TextField name={'businessDomain'} klass={'text'}
                                                                value={cachedCompanyInfo.businessDomain} handleChange={handleChange} />} />
          <Field title={FIELD_MAPPING['accountGrade']} content={<SingleSelectDropDown items={ACCOUNT_GRADES} klass={'dropdown'} onSelect={handleAccountGradeSelect}
                                                                        name={'accountGrade'} searchable={false}
                                                                        selected={ACCOUNT_GRADES.find(option => option.value === cachedCompanyInfo.accountGrade)} />} />
          <Field title={FIELD_MAPPING['isActive']} content={<SingleSelectDropDown items={ACTIVE_STATUS} klass={'dropdown'} onSelect={handleStatusSelect}
                                                                         name={'isActive'} searchable={false}
                                                                         selected={ACTIVE_STATUS.find(option => option.value === cachedCompanyInfo.isActive)} />} />
          <Field title={FIELD_MAPPING['sellerType']} content={<SingleSelectDropDown items={SELLER_TYPES} klass={'dropdown'} onSelect={handleAdInventorySelect}
                                                                       name={'sellerType'} searchable={false}
                                                                       selected={SELLER_TYPES.find(option => option.value === cachedCompanyInfo.sellerType)} />} />
          <Field title={FIELD_MAPPING['streetAddress']} content={<TextField name={'streetAddress'} klass={'text'}
                                                               value={cachedCompanyInfo.streetAddress} handleChange={handleChange} />} />
          <Field title={FIELD_MAPPING['city']} content={<TextField name={'city'} klass={'text'}
                                                    value={cachedCompanyInfo.city} handleChange={handleChange}/>} />
          <Field title={FIELD_MAPPING['region']} content={<TextField name={'region'} klass={'text'}
                                                            value={cachedCompanyInfo.region} handleChange={handleChange} />} />
          <Field title={FIELD_MAPPING['zipCode']} content={<TextField name={'zipCode'} klass={'text'}
                                                                    value={cachedCompanyInfo.zipCode} handleChange={handleChange} />} />
        </SectionCard>
      </>}
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CompanyInfo;